<template>
  <View v-if="state === 'loading'" justify="center" padding="lg">
    <LoadingIndicator />
  </View>

  <View v-else-if="state === 'signed-in'" direction="column" gap="md">
    <Text variant="h1">Sign out to accept this invite</Text>
    <Text variant="body-large" color="secondary">
      You're signed in to a Tallo account on this device. Sign out, and the invite continues from here.
    </Text>
    <Button @click="signOutAndContinue">Sign out</Button>
  </View>

  <form v-else-if="state === 'valid' && invite" @submit.prevent="acceptWithCredentials">
    <FormGroup>
      <View direction="column" gap="sm">
        <Text variant="h1">{{ invite.rejoining ? 'Welcome back' : `Join ${invite.companyName}` }}</Text>
        <Text variant="body-large" color="secondary">{{ inviterLine }}</Text>
      </View>

      <View gap="sm" align="center" wrap="wrap">
        <Text variant="body-medium" color="secondary">{{ invite.rejoining ? 'Rejoining as' : 'Joining as' }}</Text>
        <Text variant="label-medium">{{ invite.email }}</Text>
        <Badge :label="companyRoleLabels[invite.role]" :variant="companyRoleBadgeVariants[invite.role]" size="small" />
      </View>

      <Text v-if="portfoliosLine" variant="body-medium" color="secondary">{{ portfoliosLine }}</Text>

      <FormField v-if="!invite.rejoining" label="Full name" :error="form.fieldErrors('name')">
        <Input v-model="form.fields.name" placeholder="Type your full name" name="name" />
      </FormField>

      <View direction="column" gap="sm">
        <FormField :label="invite.rejoining ? 'Your password' : 'Password'" :error="form.fieldErrors('password')">
          <Input
            v-model="form.fields.password"
            :placeholder="invite.rejoining ? 'The password of your existing account' : 'Create a password'"
            type="password"
            name="password"
            @focus="showPasswordStrength = true"
          />
        </FormField>

        <PasswordStrength
          v-if="!invite.rejoining"
          :show-component="showPasswordStrength"
          :password="form.fields.password"
          @password-status-change="setPasswordValidity"
        />
      </View>

      <Button type="submit" :loading="form.submitting">{{ invite.rejoining ? 'Rejoin' : 'Create account' }}</Button>

      <TermsNotice v-if="!invite.rejoining" />

      <FormSeparator />
      <GoogleCredentialButton text="continue_with" @credential="acceptWithGoogle" />
      <Text v-if="invite.rejoining" variant="body-small" color="secondary" align="center">
        Signed up with Google? Use Continue with Google.
      </Text>

      <Hint v-if="acceptError" type="error" icon="alert-triangle">{{ acceptError }}</Hint>
    </FormGroup>
  </form>

  <View v-else-if="closedCopy" direction="column" gap="md" align="start">
    <IconTile :icon="closedCopy.icon" size="large" :color="closedCopy.color" shape="rounded" colored-icon />
    <Text variant="h1">{{ closedCopy.title }}</Text>
    <Text variant="body-large" color="secondary">{{ closedCopy.description }}</Text>
    <Button v-if="state === 'unavailable'" variant="outline" @click="load">Try again</Button>
    <Button v-else :variant="state === 'accepted' ? 'primary' : 'outline'" :to="signInRoute()">
      {{ state === 'accepted' ? 'Sign in' : 'Go to sign in' }}
    </Button>
  </View>
</template>

<script setup lang="ts">
import { required, requiredIf } from '@vuelidate/validators';
import { AxiosError } from 'axios';
import { storeToRefs } from 'pinia';
import pluralize from 'pluralize';
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import { SignInResultDto, useAuthStore } from '@tallo/auth';
import {
  Badge,
  Button,
  FormField,
  FormGroup,
  FormSeparator,
  Hint,
  IconName,
  IconTile,
  IconTileColor,
  Input,
  LoadingIndicator,
  PasswordStrength,
  Text,
  View,
  extractErrorMessage,
  handleError,
  useForm,
} from '@tallo/design-system';
import {
  TeamInviteDetailsDto,
  TeamInviteState,
  companyRoleBadgeVariants,
  companyRoleLabels,
  isClosedInviteError,
  teamInviteService,
} from '@tallo/investor/company';
import { signInRoute } from '@tallo/investor/navigation';

import { fullNameValidator } from './full-name.validator';
import GoogleCredentialButton from './google-credential-button.vue';
import TermsNotice from './terms-notice.vue';

/** The invite's own states plus the page's: loading, blocked by a session, unknown link, or unreachable */
type ClosedState = 'expired' | 'revoked' | 'accepted' | 'not-found' | 'unavailable';
type PageState = 'loading' | 'signed-in' | 'valid' | ClosedState;

const pageStateByInviteState: Record<TeamInviteState, PageState> = {
  [TeamInviteState.VALID]: 'valid',
  [TeamInviteState.EXPIRED]: 'expired',
  [TeamInviteState.REVOKED]: 'revoked',
  [TeamInviteState.ACCEPTED]: 'accepted',
};

const route = useRoute();
const router = useRouter();
const authStore = useAuthStore();
const { isAuthenticated } = storeToRefs(authStore);

const token = typeof route.query.token === 'string' ? route.query.token : undefined;
const state = ref<PageState>('loading');
const invite = ref<TeamInviteDetailsDto>();
const acceptError = ref('');
const showPasswordStrength = ref(false);
const passwordValid = ref(false);

const closedCopy = computed<{ icon: IconName; color: IconTileColor; title: string; description: string } | undefined>(
  () => {
    const inviter = invite.value?.inviterName || 'the person who invited you';
    const company = invite.value?.companyName || 'the company';

    switch (state.value) {
      case 'expired':
        return {
          icon: 'clock-rewind',
          color: 'yellow',
          title: 'This invite has expired',
          description: `Ask ${inviter} to send you a new one.`,
        };
      case 'revoked':
        return {
          icon: 'x-close-circle',
          color: 'red',
          title: 'This invite is no longer active',
          description: `Ask an admin at ${company} for a new one.`,
        };
      case 'accepted':
        return {
          icon: 'check-circle',
          color: 'green',
          title: 'This invite was already used',
          description: invite.value ? `Sign in as ${invite.value.email} to continue.` : 'Sign in to continue.',
        };
      case 'not-found':
        return {
          icon: 'alert-triangle',
          color: 'red',
          title: 'Invite not found',
          description: 'Use the full link from the email; it may have been cut off.',
        };
      case 'unavailable':
        return {
          icon: 'alert-triangle',
          color: 'yellow',
          title: "Couldn't load this invite",
          description: 'Check your connection and try again.',
        };
      default:
        return undefined;
    }
  },
);

const inviterLine = computed(() =>
  invite.value?.rejoining
    ? `${invite.value.inviterName} invited you back to ${invite.value.companyName}. Sign in with your existing account to rejoin.`
    : `${invite.value?.inviterName} invited you to join ${invite.value?.companyName} on Tallo.`,
);

const portfoliosLine = computed(() => {
  const portfolios = invite.value?.collaboratePortfolios ?? [];

  if (!portfolios.length) return '';

  const names = portfolios.map(
    (portfolio) => `${portfolio.name} (${pluralize('property', portfolio.unitCount, true)})`,
  );

  return `You'll work on ${names.join(', ')}.`;
});

// A rejoining member proves an existing password; only a new account picks a name and a strong password
const form = useForm(
  { name: '', password: '' },
  computed(() => ({
    name: { required: requiredIf(() => !invite.value?.rejoining), fullName: fullNameValidator },
    password: { required, strong: () => Boolean(invite.value?.rejoining) || passwordValid.value },
  })),
);

function setPasswordValidity(valid: boolean) {
  passwordValid.value = valid;
}

async function load() {
  if (!token) {
    state.value = 'not-found';
    return;
  }

  state.value = 'loading';

  try {
    invite.value = await teamInviteService.getDetails(token);
    state.value = pageStateByInviteState[invite.value.state];
  } catch (error) {
    const status = error instanceof AxiosError ? error.response?.status : undefined;
    state.value = status === 404 || status === 400 ? 'not-found' : 'unavailable';
  }
}

async function signOutAndContinue() {
  authStore.logout();
  await load();
}

async function finishSignIn(result: SignInResultDto) {
  // A two-factor account is caught by the router guard on the way, which keeps this destination for after the code
  authStore.handleSignInResult(result);
  // Home's guard sends a newcomer through their onboarding steps first, as it does after any sign-in
  await router.push(authStore.consumePostSignInRedirect() ?? { path: '/' });
}

/** Accepting can fail because the invite closed meanwhile; the page then reads it again and shows why */
async function handleAcceptError(error: unknown, fallback: string, onRefused: (message: string) => void) {
  if (isClosedInviteError(error)) {
    await load();
    return;
  }

  if (error instanceof AxiosError && error.response?.status === 404) {
    state.value = 'not-found';
    return;
  }

  if (error instanceof AxiosError && (error.response?.status === 401 || error.response?.status === 409)) {
    onRefused(extractErrorMessage(error, fallback));
    return;
  }

  handleError(error);
}

async function acceptWithCredentials() {
  if (!token) return;

  acceptError.value = '';

  try {
    await form.submit(async () => {
      try {
        const result = await teamInviteService.acceptWithCredentials({
          token,
          name: invite.value?.rejoining ? undefined : form.fields.name.trim(),
          password: form.fields.password,
        });
        await finishSignIn(result);
      } catch (error) {
        await handleAcceptError(error, 'That password does not match the account for this email.', (message) =>
          error instanceof AxiosError && error.response?.status === 401
            ? form.setFieldError('password', message)
            : (acceptError.value = message),
        );
      }
    });
  } catch {
    // An invalid form shows its field errors
  }
}

async function acceptWithGoogle(googleJwt: string) {
  if (!token) return;

  acceptError.value = '';

  try {
    const result = await teamInviteService.acceptWithGoogle({ token, googleJwt });
    await finishSignIn(result);
  } catch (error) {
    await handleAcceptError(
      error,
      `Sign in with ${invite.value?.email ?? 'the invited account'} to accept this invite.`,
      (message) => (acceptError.value = message),
    );
  }
}

// A stale or removed session on this device is signed out by the app's first request; the page then carries on
watch(isAuthenticated, (signedIn) => {
  if (!signedIn && state.value === 'signed-in') {
    load();
  }
});

onMounted(() => {
  if (isAuthenticated.value) {
    state.value = 'signed-in';
    return;
  }

  load();
});
</script>
